import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Language } from '../../constants/language';
import { configurationService } from '../../services';
import { cn } from '../../utils/class-names-util';

interface LanguageSelectorProps {
  isScrolled?: boolean;
  variant?: 'header' | 'footer';
  className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  isScrolled = false,
  variant = 'header',
  className = ''
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLanguage = i18n.language as Language;

  const changeLanguage = (lang: Language.EN | Language.AR) => {
    // Update i18next
    i18n.changeLanguage(lang);
    
    // Update configuration service cache
    configurationService.setLanguage(lang as Language);
    
    // Close dropdown
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const languages = [
    { code: Language.EN, name: 'English', nativeName: 'English', flag: 'US' },
    { code: Language.AR, name: 'Arabic', nativeName: 'العربية', flag: 'EG' }
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[1]; // Default to Arabic

  if (variant === 'footer') {
    return (
      <div className={`flex space-x-2 ${className}`}>
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={cn(
              'px-3 py-1 text-sm font-medium rounded-lg transition-all duration-200',
              currentLanguage === lang.code
                ? 'bg-white text-black'
                : 'text-gray-300 hover:text-white hover:bg-white/10'
            )}
          >
            {lang.nativeName}
          </button>
        ))}
      </div>
    );
  }

  // Header variant with professional dropdown
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center space-x-2 px-3 py-2 text-sm font-medium transition-all duration-300 rounded-lg border',
          isScrolled 
            ? 'text-black hover:text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50' 
            : 'text-white hover:text-gray-200 border-white/20 hover:border-white/40 hover:bg-white/10'
        )}
      >
        <span className="text-xs font-bold px-1 py-0.5 bg-black/10 rounded text-center min-w-[24px]">{currentLang.flag}</span>
        <span className="hidden sm:block">{currentLang.nativeName}</span>
        <svg 
          className={cn(
            'w-4 h-4 transition-transform duration-200',
            isOpen && 'rotate-180'
          )} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={cn(
          'absolute top-full right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50',
          'animate-in slide-in-from-top-2 duration-200'
        )}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={cn(
                'w-full flex items-center space-x-3 px-4 py-3 text-left text-sm font-medium transition-all duration-200 hover:bg-gray-50',
                currentLanguage === lang.code
                  ? 'text-black bg-gray-50'
                  : 'text-gray-700 hover:text-black'
              )}
            >
              <span className="text-xs font-bold px-1 py-0.5 bg-gray-100 rounded text-center min-w-[24px]">{lang.flag}</span>
              <div className="flex-1">
                <div className="font-medium">{lang.nativeName}</div>
                <div className="text-xs text-gray-500">{lang.name}</div>
              </div>
              {currentLanguage === lang.code && (
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;